import type { BrowserAgent } from '@newrelic/browser-agent';
import { SEVERITY, type LoggerAdapter, type LogRecord, type Severity } from '../logger-types';

declare global {
  interface Window {
    /**
     * The inline New Relic Browser Agent in `index.html` exposes itself as
     * `window.newrelic`. The runtime version is pinned in HTML; the type comes
     * from the matching `@newrelic/browser-agent` dev-dependency. When the
     * inline snippet is updated, bump the npm package to the same version.
     */
    newrelic?: BrowserAgent;
  }
}

type NRLogLevel = NonNullable<NonNullable<Parameters<BrowserAgent['log']>[1]>['level']>;

const SEVERITY_TO_NR_LEVEL: Record<Severity, NRLogLevel> = {
  [SEVERITY.ERROR]: 'ERROR',
  [SEVERITY.WARN]: 'WARN',
  [SEVERITY.INFO]: 'INFO',
  [SEVERITY.DEBUG]: 'DEBUG',
};

export class NewRelicLoggerAdapter implements LoggerAdapter {
  // window.newrelic is read on every emit because the inline agent in
  // index.html sets the global slightly after HTML parse.
  emit(record: LogRecord): void {
    const nr = typeof window !== 'undefined' ? window.newrelic : undefined;
    if (!nr) {
      return;
    }

    const customAttributes = { 'code.namespace': record.scope, ...record.attributes };

    if (record.severity === SEVERITY.ERROR) {
      nr.noticeError(buildError(record), customAttributes);
      return;
    }

    nr.log(record.body, { level: SEVERITY_TO_NR_LEVEL[record.severity], customAttributes });
  }
}

function buildError(record: LogRecord): Error {
  const message =
    typeof record.attributes['exception.message'] === 'string'
      ? record.attributes['exception.message']
      : record.body;

  const error = new Error(message);

  const type = record.attributes['exception.type'];
  if (typeof type === 'string') {
    error.name = type;
  }

  const stack = record.attributes['exception.stacktrace'];
  if (typeof stack === 'string') {
    error.stack = stack;
  }

  return error;
}
